import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Field, FormSection } from "redux-form";

import { ReduxControls, ErrorBanner } from "rhombus-react";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import AssetSelect from "social/AssetSelect";

import { getErrorMessagesByAdType } from "selectors/Social2";

import { TWITTER_CONVERSATION_CONTROL_OPTIONS } from "social/constants/common";
import { prettifyMessages } from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { TWITTER_IMAGE_CONSTRAINTS } from "social/validators/twitter";

const {
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_TWEET,
	MAX_LENGTH_LANDING_PAGE,
	MAX_IMAGE_MB,
	IMAGE_ACCEPT,
	DIMENSIONS,
} = TWITTER_IMAGE_CONSTRAINTS;

const TWITTER_IMAGE_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `${MAX_IMAGE_MB}MB Maximum`,
	},
	{
		name: "File Types:",
		description: IMAGE_ACCEPT,
	},
	{
		name: "Dimensions:",
		description: `1.91:1 or 1:1 aspect ratio ${DIMENSIONS.minWidth}px min width`,
	}
];


const TWITTER_IMAGE_FIELDS = {
	image: {
		name: "image",
		label: "Image",
		className: "full-width",
		required: true,
		assetType: ASSET_TYPES.IMAGE,
		component: AssetSelect,
		requirements: TWITTER_IMAGE_REQUIREMENTS,
		accept: IMAGE_ACCEPT,
	},
	name: {
		name: "name",
		label: "Creative Name",
		className: "full-width",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
	},
	headline: {
		name: "headline",
		label: "Headline",
		className: "full-width",
		placeholder: "Enter a short headline",
		tip: {
			description: "May get truncated after 50 characters",
			direction: "right",
		},
		component: ReduxControls.Input,
		charMax: MAX_LENGTH_HEADLINE,
		showCharCount: true,
		required: true,
	},
	tweet: {
		name: "tweet",
		label: "Tweet",
		className: "full-width",
		placeholder: "Enter the tweet copy",
		component: ReduxControls.Input,
		charMax: MAX_LENGTH_TWEET,
		showCharCount: true,
		required: true,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		className: "full-width",
		placeholder: "https://www.example.com",
		component: ReduxControls.Input,
		charMax: MAX_LENGTH_LANDING_PAGE,
		showCharCount: true,
		required: true,
	},
	conversationControl: {
		name: "conversationControl",
		label: "Conversation Control",
		className: "half-width",
		component: ReduxControls.Select,
		options: TWITTER_CONVERSATION_CONTROL_OPTIONS,
		normalize: option => option.value,
		format: value => ({ value, label: value }),
		isClearable: false,
		menuPlacement: "top",
	},
};

const adType = SOCIAL_AD_TYPES.TWITTER_IMAGE;

export default function TwitterImage(props){
	const { navigationActions, libraryId, isViewOnly, showPristineErrors } = props;

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, TWITTER_IMAGE_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if(hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = Object.values(TWITTER_IMAGE_FIELDS).map((fieldProps) => {
		const extraProps = {};

		if(fieldProps.name === "image") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
		}

		extraProps.disabled = isViewOnly;

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}
				</div>
			</FormSection>
		</div>
	);
}

TwitterImage.displayName = "TwitterImageForm";
